'use client';
import { IconShare, IconShareLink } from '@moar/svg';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { useEffect, useState } from 'react';
import { useIsOnline } from '../context/online';

// text used on detail page, button used on glossary
export interface ShareMenuProps {
    buttonMode?: 'text' | 'button';
    title?: string;
}

export function ShareMenu(props: ShareMenuProps) {
    const { buttonMode = 'text', title } = props;
    const isOnline = useIsOnline();
    const [facebookUrl, setFacebookUrl] = useState('');
    const [twitterUrl, setTwitterUrl] = useState('');
    const [emailUrl, setEmailUrl] = useState('');

    useEffect(() => {
        // prefer the title we were given: document.title is set by next/head after this runs, so on a
        // client side navigation it can still be the previous page's title
        const shareTitle = encodeURIComponent(title || document.title);
        // the page itself, without transient params - the glossary carries ?back= and a #term anchor
        const url = encodeURIComponent(`${window.location.origin}${window.location.pathname}`);

        // Used hook to make sure component is rendered on UI before looking for window.location or error
        setFacebookUrl(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
        setTwitterUrl(`https://twitter.com/intent/tweet?text=${shareTitle}&url=${url}&via=amrevmuseum`);
        setEmailUrl(`mailto:?subject=${shareTitle}&body=${url}`);
    }, [title]);

    if (!isOnline) return null;

    return (
        <div>
            <Menu>
                {buttonMode === 'text' && (
                    <MenuButton className="flex items-center group" aria-label="Share">
                        <IconShareLink />
                        <span className="mt-1 ml-2 text-sm font-bold text-blue group-hover:text-midnight">Share</span>
                    </MenuButton>
                )}
                {buttonMode === 'button' && (
                    <MenuButton
                        className="w-[36px] h-[36px] bg-white rounded-sm flex items-center justify-center hover:bg-ivory-dark"
                        aria-label="Share"
                    >
                        <IconShare />
                    </MenuButton>
                )}

                <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    {/* modal={false}: headlessui v2 treats an open menu like a dialog by default,
                        locking page scrolling (overflow: hidden on html) and making the rest of the
                        page inert. That is wrong for a share dropdown - you should still be able to
                        scroll the page while it is open. */}
                    <MenuItems
                        modal={false}
                        className="absolute right-0 z-40 w-40 mt-2 origin-top-right bg-white border shadow-lg border-ash ring-1 ring-black ring-opacity-5 focus:outline-none"
                    >
                        <div className="">
                            <ul className="divide-y divide-[#ccc]">
                                <MenuItem>
                                    <li className="">
                                        <a
                                            aria-label="Share via Twitter"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block px-4 py-3 text-base-plus hover:bg-[#ecf0f5]"
                                            href={twitterUrl}
                                        >
                                            Twitter
                                        </a>
                                    </li>
                                </MenuItem>
                                <MenuItem>
                                    <li className="">
                                        <a
                                            aria-label="Share via Facebook"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block px-4 py-3 text-base-plus hover:bg-[#ecf0f5]"
                                            href={facebookUrl}
                                        >
                                            Facebook
                                        </a>
                                    </li>
                                </MenuItem>
                                <MenuItem>
                                    <li className="">
                                        <a
                                            aria-label="Share via Email"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block px-4 py-3 text-base-plus hover:bg-[#ecf0f5]"
                                            href={emailUrl}
                                        >
                                            Email
                                        </a>
                                    </li>
                                </MenuItem>
                            </ul>
                        </div>
                    </MenuItems>
                </Transition>
            </Menu>
        </div>
    );
}

export default ShareMenu;
